/**
 * Web Push (FCM) client helper.
 *
 * Public API:
 *   - getWebPushStatus()   → "granted" | "denied" | "default" | "unsupported"
 *   - enableWebPush()      → asks for permission, gets FCM token, registers it on the backend.
 *   - disableWebPush()     → unsubscribes locally and notifies backend (best effort).
 *
 * Token is registered through the existing mobile devices endpoint with
 * `platform:"web"` so the FCM admin code (services/push_fcm.py) treats web
 * subscribers exactly like mobile devices.
 */
import { initializeApp, getApps } from "firebase/app";
import { getMessaging, getToken, deleteToken, onMessage, isSupported } from "firebase/messaging";
import axios from "axios";
import { toast } from "sonner";
import { API_V1 } from "./api";

const FIREBASE_CONFIG = {
  apiKey: "AIzaSyDT83vWAkoK-nSdNtCbkmXgsD35UlzH4EE",
  authDomain: "cms-green.firebaseapp.com",
  projectId: "cms-green",
  storageBucket: "cms-green.firebasestorage.app",
  messagingSenderId: "326608376370",
  appId: "1:326608376370:web:bb1d93ecf025599762a22b",
};

// VAPID public key for Web Push (set by Firebase Cloud Messaging → Web Push certificates).
// Keep in sync with backend FCM_VAPID_KEY env.
const VAPID_KEY = "c-Iz2lgA9YlXK-d4FrBPYmVvyTVXsTpiVQ960smWXGk";

let _app = null;
let _messagingPromise = null;

function ensureApp() {
  if (_app) return _app;
  _app = getApps().length > 0 ? getApps()[0] : initializeApp(FIREBASE_CONFIG);
  return _app;
}

async function getMessagingSafe() {
  if (_messagingPromise) return _messagingPromise;
  _messagingPromise = (async () => {
    const supported = await isSupported().catch(() => false);
    if (!supported) return null;
    return getMessaging(ensureApp());
  })();
  return _messagingPromise;
}

export function getWebPushStatus() {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  if (!("serviceWorker" in navigator)) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

async function registerWithBackend(token) {
  // Unique-per-browser device id stored in localStorage so re-enabling refreshes
  // the same record instead of creating duplicates.
  let deviceId = localStorage.getItem("web_push_device_id");
  if (!deviceId) {
    deviceId = `web_${crypto.randomUUID ? crypto.randomUUID() : Date.now() + "_" + Math.random().toString(36).slice(2)}`;
    localStorage.setItem("web_push_device_id", deviceId);
  }
  await axios.post(`${API_V1.replace("/v1", "")}/mobile/v1/devices/register`, {
    device_id: deviceId,
    push_token: token,
    platform: "web",
    model: navigator.userAgent.slice(0, 80),
    os_version: navigator.platform || "browser",
    app_version: "crm-web",
  });
  localStorage.setItem("web_push_token", token);
}

export async function enableWebPush() {
  const status = getWebPushStatus();
  if (status === "unsupported") {
    toast.error("Tento prohlížeč nepodporuje webové notifikace.");
    return false;
  }
  if (status === "denied") {
    toast.error("Notifikace jsou zablokovány. Povolte je v nastavení prohlížeče (zámek vedle adresy).");
    return false;
  }
  try {
    const messaging = await getMessagingSafe();
    if (!messaging) {
      toast.error("Firebase Messaging není v tomto prohlížeči podporován.");
      return false;
    }
    // Make sure SW is registered (CRA serves /firebase-messaging-sw.js as static)
    const swReg = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });
    if (!token) {
      toast.error("FCM token se nepodařilo získat — zkontrolujte oprávnění.");
      return false;
    }
    await registerWithBackend(token);
    toast.success("Web push aktivován ✓ Notifikace o nové poptávce dorazí i v prohlížeči.");
    return true;
  } catch (e) {
    console.error("enableWebPush error:", e);
    toast.error("Aktivace selhala: " + (e?.message || "neznámá chyba"));
    return false;
  }
}

export async function disableWebPush() {
  try {
    const messaging = await getMessagingSafe();
    if (messaging) await deleteToken(messaging).catch(() => {});
    // Best-effort: tell backend to forget this device (delete endpoint already exists).
    const deviceId = localStorage.getItem("web_push_device_id");
    if (deviceId) {
      await axios.delete(`${API_V1.replace("/v1", "")}/mobile/v1/devices/${deviceId}`).catch(() => {});
    }
    localStorage.removeItem("web_push_token");
    toast.success("Web push deaktivován");
    return true;
  } catch (e) {
    toast.error("Deaktivace selhala: " + (e?.message || "neznámá chyba"));
    return false;
  }
}

/** Subscribe to foreground messages (the page is open & focused). */
export async function onForegroundMessage(handler) {
  const messaging = await getMessagingSafe();
  if (!messaging) return () => {};
  return onMessage(messaging, handler);
}

export function isWebPushEnabledLocally() {
  return !!localStorage.getItem("web_push_token") && getWebPushStatus() === "granted";
}
